import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useTheme } from '@mui/material';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import * as React from 'react';
import { Box, FlexBox } from '../box';
import { IconButton } from '../button';
import { IconExpandMore } from '../icon';
import { Menu } from '../menu';
import { styled } from '../styled';
import { getPaletteColor } from '../theme';
import { Tooltip } from '../tooltip';
import { useBackgroundColorForLevel } from './hooks';
import { getSlotGridTemplate } from './utils';
const NestingLevelContext = React.createContext(0);
const StyledAccordion = styled(MuiAccordion, {
    shouldForwardProp: (prop) => prop !== 'level' &&
        prop !== 'background' &&
        prop !== 'bgcolor' &&
        prop !== 'noPadding'
})(({ theme, background, noPadding }) => ({
    backgroundColor: background,
    border: `1px solid ${theme.palette.divider}`,
    boxSizing: 'border-box',
    padding: noPadding ? 0 : theme.spacing(2),
    boxShadow: 'none',
    borderRadius: theme.shape.borderRadius,
    width: '100%',
    '&:before': {
        display: 'none'
    },
    '&.Mui-expanded': {
        margin: 0
    }
}));
const AccordionSummaryBase = React.forwardRef((props, ref) => _jsx(MuiAccordionSummary, { component: "div", ref: ref, ...props }));
const StyledAccordionSummary = styled(AccordionSummaryBase)(({ theme }) => ({
    padding: 0,
    flexDirection: 'row-reverse',
    '& .MuiAccordionSummary-content': {
        margin: 0,
        alignItems: 'center',
        gap: theme.spacing(2),
        width: '100%',
        '&.Mui-expanded': {
            margin: 0
        }
    },
    '& .MuiAccordionSummary-expandIconWrapper': {
        marginRight: theme.spacing(2)
    }
}));
const StyledAccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
    padding: 0,
    paddingTop: theme.spacing(2),
    gap: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column'
}));
const StyledExpandIcon = styled(IconExpandMore)({
    transition: 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1)'
});
export const StyledIconButtonWrapper = styled('div')(({ theme }) => ({
    width: theme.spacing(5),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
}));
export const SlotsWrapper = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'gridTemplate'
})(({ gridTemplate, theme }) => ({
    display: 'grid',
    gridTemplateColumns: gridTemplate,
    alignItems: 'center',
    gap: theme.spacing(2),
    flex: 1,
    overflow: 'hidden'
}));
function AccordionTreeActions(props) {
    const { data, level, actions, groupActions } = props;
    if (!actions && !groupActions) {
        return null;
    }
    return (_jsxs(FlexBox, { hAlign: "flex-end", children: [actions?.map((action, key) => {
                if (action.visible?.(data, level) === false) {
                    return null;
                }
                const isDisabled = action.disabled?.(data, level) ?? false;
                return (_jsx(FlexBox, { hAlign: "center", children: _jsx(Tooltip, { title: action.primary, placement: "top", children: _jsx(StyledIconButtonWrapper, { children: _jsx(IconButton, { icon: action.icon, iconProps: action.iconProps, color: isDisabled ? undefined : 'primary', disabled: isDisabled, onClick: (e) => {
                                    e.stopPropagation();
                                    action.onClick(data, level);
                                } }) }) }) }, key));
            }), groupActions && (_jsx(StyledIconButtonWrapper, { children: _jsx(Menu, { actions: groupActions, item: data }) }))] }));
}
function AccordionTreeSlotsRenderer(props) {
    const { data, level, isExpanded, slots } = props;
    const gridTemplate = getSlotGridTemplate(slots);
    return (_jsx(SlotsWrapper, { gridTemplate: gridTemplate, children: slots.map((slot, i) => {
            const content = slot.component(data, level, isExpanded);
            return (_jsx(FlexBox, { overflow: "hidden", children: content }, slot.id || i));
        }) }));
}
function AccordionTreeSummaryContent(props) {
    const { item, level, isExpanded, slots, actions, groupActions } = props;
    if (item.contentComponent) {
        return _jsx(_Fragment, { children: item.contentComponent() });
    }
    return (_jsxs(_Fragment, { children: [_jsx(AccordionTreeSlotsRenderer, { data: item.data, level: level, isExpanded: isExpanded, slots: slots }), _jsx(AccordionTreeActions, { data: item.data, level: level, actions: actions, groupActions: groupActions })] }));
}
export function TreeNode(props) {
    const { item, level, slots, actions, groupActions, expandedKeys, onToggle, componentSlots, slotProps } = props;
    const hasChildren = (item.children && item.children.length > 0) || !!item.childComponent;
    const isExpanded = expandedKeys.has(item.key);
    const theme = useTheme();
    const backgroundColor = useBackgroundColorForLevel(level);
    const bgColor = item.contentComponent
        ? getPaletteColor(theme, 'components.tree-elevation-base')
        : backgroundColor;
    const handleToggle = (event) => {
        if (hasChildren) {
            event.stopPropagation();
            onToggle(item.key);
        }
    };
    const AccordionComponent = componentSlots?.accordion ?? StyledAccordion;
    const AccordionSummaryComponent = componentSlots?.accordionSummary ?? StyledAccordionSummary;
    const AccordionDetailsComponent = componentSlots?.accordionDetails ?? StyledAccordionDetails;
    const expandIcon = (_jsx(StyledIconButtonWrapper, { children: hasChildren && _jsx(StyledExpandIcon, { role: "button" }) }));
    return (_jsx(NestingLevelContext.Provider, { value: level, children: _jsxs(AccordionComponent, { level: level, background: bgColor, noPadding: !!item.contentComponent, expanded: hasChildren ? isExpanded : false, onChange: handleToggle, disableGutters: true, ...slotProps?.accordion, children: [_jsx(AccordionSummaryComponent, { expandIcon: expandIcon, ...slotProps?.accordionSummary, children: _jsx(AccordionTreeSummaryContent, { item: item, level: level, isExpanded: isExpanded, slots: slots, actions: actions, groupActions: groupActions }) }), hasChildren && (_jsxs(AccordionDetailsComponent, { ...slotProps?.accordionDetails, children: [item.childComponent && (_jsx(FlexBox, { width: "100%", gap: 2, bgcolor: "components.tree-elevation-base", border: `1px solid ${theme.palette.divider}`, padding: 4, borderRadius: 1, children: item.childComponent() })), item.children?.map((child) => (_jsx(TreeNode, { item: child, level: level + 1, slots: slots, actions: actions, groupActions: groupActions, expandedKeys: expandedKeys, onToggle: onToggle, componentSlots: componentSlots, slotProps: slotProps }, child.key)))] }))] }) }));
}
